/* eslint complexity: ["error", 13] */
import TitleAndNameSection from './Components/TitleAndNameSection';
import Main from '../../components/Main';
import { Box, BRANDS, Paragraph, useBrand } from '@cof/plastic-components';
import { withText } from '../../text/textStore';
import EmploymentSection from './Components/EmploymentSection';
import AddressSection from './Components/AddressSection';
import FinancialDependencySection from './Components/FinancialDependencySection';
import { useFormContext } from 'react-hook-form';
import ProgressBarSinglePage from '../../components/ProgressBarSinglePage/ProgressBarSinglePage';
import { css, styled } from 'styled-components';
import { StyledFormWrapper } from './StyledComponents';
import { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { setShowErrors } from './Components/ErrorHandler';
import { useProgressBar } from './Components/useProgressBar';
import { useLandmarkRefsContext } from '../../components/LandmarkRefsProvider';
import PropTypes from 'prop-types';
import { FORM_SECTIONS, SECTION_IDS } from './Components/FormIDs';
import { useLocation } from 'react-router';
import partners from '../../partners';
import ApplySection from './Components/ApplySection';
import { AsosSectionHeading, SingleFormWrapper } from './Components/SharedStyledComponents';
import { useFormData } from '../../components/FormDataContext';
import { useZuko } from '../../utilities/useZuko';
import config from '@cof/ukweb-config/lib/clientConfig';
import { addNewRelicPageAction } from '../../utilities/newRelic';
import { useQuantumMetric } from '../../utilities/useQuantumMetric';
import withExperiment from '../../components/experiments/withExperiment';

export const CenteredContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 67.4rem;
    margin: auto;
    box-sizing: content-box;
    margin-bottom: 32px;
    border-radius: 4px;
`;

const FormContainer = styled(Box)`
    ${({ theme }: any) => css`
        text-align: left;
        max-width: 67.4rem;
        margin: auto;
        box-sizing: content-box;
        padding: 0 16px;
        margin-bottom: ${theme.space.lg};
    `};
`;

const NoTopRadiusFormWrapper = styled(SingleFormWrapper)`
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`;

export const FormUtilsContext = createContext(null);

const getSectionFromPath = (path: any) => {
    if (!path) return null;
    if (path.startsWith('landing-screen')) return SECTION_IDS.ABOUT_YOU;
    if (path.startsWith('income')) return SECTION_IDS.EMPLOYMENT;
    if (path.startsWith('outgoings')) return SECTION_IDS.OUTGOINGS;
    if (path.startsWith('address')) return SECTION_IDS.ADDRESS;
    return null;
};

const ExperimentObOptIn = withExperiment(() => null, 'quotations-ob-accept-region-opt-in-mbox');

// Removes previous-address data left over in form state if the current-address duration no longer requires it
const stripUnneededPreviousAddress = (formData: any) => {
    const yearsAtCurrent = formData[FORM_SECTIONS.Address.Current.YearsAtAddress];
    const previousAddressRequired = yearsAtCurrent !== undefined && parseInt(yearsAtCurrent, 10) < 2;
    if (!previousAddressRequired) {
        Object.values(FORM_SECTIONS.Address.Previous).forEach((key) => delete formData[key]);
    }
};

const QuickCheck = ({ Text, onSubmit, monitoringParams }: any) => {
    const [showManualForm, setShowManualForm] = useState({ current: false, previous: false });
    const location = useLocation();
    const aboutYouRef = useRef(null);
    const employmentRef = useRef(null);
    const outgoingsRef = useRef(null);
    const addressRef = useRef(null);

    const sectionRefs = {
        [SECTION_IDS.ABOUT_YOU]: aboutYouRef,
        [SECTION_IDS.EMPLOYMENT]: employmentRef,
        [SECTION_IDS.OUTGOINGS]: outgoingsRef,
        [SECTION_IDS.ADDRESS]: addressRef
    };

    const { isEdit } = useFormData();

    // @ts-expect-error TS2339: Property 'setSubmitAttempted' does not exist on type ''.
    const { setSubmitAttempted, formErrorsRef } = useLandmarkRefsContext();

    useEffect(() => {
        const pathName = location.pathname.substring(1);
        const targetSection = getSectionFromPath(pathName);
        if (targetSection && sectionRefs[targetSection]?.current) {
            // @ts-expect-error TS2339: Property 'scrollIntoView' does not exist on type 'never'.
            sectionRefs[targetSection].current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    const { setFormEl, trackCompletionEvent, trackValidationError } = useZuko(monitoringParams);

    useQuantumMetric(monitoringParams);

    const { currentStageIndex, totalStages } = useProgressBar();

    const [fieldsTouched, setFieldsTouched] = useState(false);

    const brand = useBrand();

    const methods = useFormContext();
    const {
        formState: { isDirty, errors }
    } = methods;

    const shouldShowProgressBar = isDirty;

    const handleQuickCheck = async () => {
        addNewRelicPageAction('Submit button clicked');
        setFieldsTouched(true);
        setShowErrors(true);

        const result = await methods.trigger();
        setSubmitAttempted(true);
        if (!result) {
            // TODO: Add unit tests for this tracking stuff
            Object.keys(errors).forEach((field) => {
                addNewRelicPageAction('submit-validation-error', { error: field });
                if (config.get('features.trackValidationErrorsOnSubmit')) trackValidationError(field);
            });
            if (formErrorsRef.current) {
                formErrorsRef.current.focus();
            }
        }
        if (result) {
            const formData = methods.getValues();
            Object.keys(formData).forEach((key) => {
                if (formData[key] === null) {
                    delete formData[key];
                }
            });
            formData[FORM_SECTIONS.Employment.OtherIncomeAmount] =
                formData[FORM_SECTIONS.Employment.OtherIncomeAmount] || '0';
            // TODO: Further improvement - once the formIDs are split into submit fields and helpers, we can delete these dynamically
            delete formData[FORM_SECTIONS.Employment.OtherIncome];
            delete formData[FORM_SECTIONS.Address.Current.LookupPostcode];
            delete formData[FORM_SECTIONS.Address.Current.HouseFlatNumber];
            stripUnneededPreviousAddress(formData);
            addNewRelicPageAction('Form Completed'); // TODO: Add unit tests for this tracking stuff
            trackCompletionEvent();
            // @ts-expect-error TS2339: Property 'adobe' does not exist on type 'Window & typeof globalThis'.
            if (window?.config?.adobeTarget?.isEnabled && typeof window?.adobe?.target?.trackEvent === 'function') {
                // @ts-expect-error TS2339: Property 'adobe' does not exist on type 'Window & typeof globalThis'.
                window.adobe.target.trackEvent({
                    mbox: 'quotations-submission'
                });
            }
            onSubmit(formData);
        }
    };

    const hasError = (id: any, errors: any, touchedFields: any, fieldsTouched: any) =>
        errors[id] && (touchedFields[id] || fieldsTouched);
    // TODO: Clean up / further improvement - can we simplify this logic by using getFieldState
    const variantTernary = useCallback(
        (id: any) => {
            const {
                formState: { errors, touchedFields },
                getValues
            } = methods;

            const hasSuccess = (id: any, touchedFields: any, fieldsTouched: any, fieldValue: any) =>
                (touchedFields[id] || fieldsTouched || isEdit) && fieldValue;

            if (hasError(id, errors, touchedFields, fieldsTouched)) return 'error';
            if (hasSuccess(id, touchedFields, fieldsTouched, getValues(id))) return 'success';
            return 'default';
        },
        [methods, fieldsTouched]
    );
    return (
        <Main>
            {/* @ts-expect-error TS2769: No overload matches this call. */}
            <StyledFormWrapper ref={setFormEl} data-qa-id="form-wrapper">
                <ProgressBarSinglePage
                    currentStageIndex={currentStageIndex}
                    stages={Array.from({ length: totalStages }, (_, i) => `stage${i + 1}`)}
                    progressBarVisible={shouldShowProgressBar}
                ></ProgressBarSinglePage>

                {/* @ts-expect-error TS2322: Type ' variantTernary: (id: any) => "default" | "error" | "success"; showManualForm:  current: boole */}
                <FormUtilsContext.Provider value={{ variantTernary, showManualForm, setShowManualForm }}>
                    <FormContainer>
                        {brand === BRANDS.ASOS.toLowerCase() && (
                            <AsosSectionHeading>A bit about you</AsosSectionHeading>
                        )}

                        <NoTopRadiusFormWrapper
                            ref={aboutYouRef}
                            id={SECTION_IDS.ABOUT_YOU}
                            data-qa-id={SECTION_IDS.ABOUT_YOU}
                        >
                            <TitleAndNameSection />
                        </NoTopRadiusFormWrapper>
                        {brand === BRANDS.ASOS.toLowerCase() && <AsosSectionHeading>Your income</AsosSectionHeading>}
                        <SingleFormWrapper
                            ref={employmentRef}
                            id={SECTION_IDS.EMPLOYMENT}
                            data-qa-id={SECTION_IDS.EMPLOYMENT}
                        >
                            <EmploymentSection />
                        </SingleFormWrapper>
                        {brand === BRANDS.ASOS.toLowerCase() && <AsosSectionHeading>Your outgoings</AsosSectionHeading>}
                        <SingleFormWrapper
                            ref={outgoingsRef}
                            id={SECTION_IDS.OUTGOINGS}
                            data-qa-id={SECTION_IDS.OUTGOINGS}
                        >
                            <FinancialDependencySection />
                        </SingleFormWrapper>
                        {brand === BRANDS.ASOS.toLowerCase() && <AsosSectionHeading>Your address</AsosSectionHeading>}
                        <SingleFormWrapper ref={addressRef} id={SECTION_IDS.ADDRESS} data-qa-id={SECTION_IDS.ADDRESS}>
                            <AddressSection />
                        </SingleFormWrapper>
                        <ExperimentObOptIn />
                        <ApplySection handleQuickCheck={handleQuickCheck} />
                        {(brand === partners.VERY || brand === partners.LITTLEWOODS) && (
                            <Paragraph size="small" pt="lg" data-qa-id="credit-broker" data-qm-allow>
                                <Text path="QuotationForm.CreditBroker" />
                            </Paragraph>
                        )}
                    </FormContainer>
                </FormUtilsContext.Provider>
            </StyledFormWrapper>
        </Main>
    );
};

QuickCheck.propTypes = {
    Text: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    monitoringParams: PropTypes.object.isRequired
};

export default withText('')(QuickCheck);
